import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { OnboardingPage } from './pages/OnboardingPage'
import { DashboardPage } from './pages/DashboardPage'
import { CertificationsPage } from './pages/CertificationsPage'
import { ExamDetailPage } from './pages/ExamDetailPage'
import { PracticePage } from './pages/PracticePage'
import { SimulationPage } from './pages/SimulationPage'
import { LabPage } from './pages/LabPage'
import { StatsPage } from './pages/StatsPage'
import { LeaderboardPage } from './pages/LeaderboardPage'
import { ProfilePage } from './pages/ProfilePage'
import { LegalPage } from './pages/LegalPage'
import { VerifyEmailPage } from './pages/VerifyEmailPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { Layout } from './components/Layout'
import {
  PlansPage,
  CheckoutPage,
  PaymentReturnPage,
  MockGatewayPage,
  SubscriptionPage,
  PaymentAdminPage,
  PaymentTransactionsPage,
  QuotaGate,
  usePaymentBootstrap,
  getRouterBasename,
} from './payment'
import { useEngagement } from './hooks/useEngagement'
import { useSyncGamification } from './hooks/useSyncGamification'

// Admin imports
import { AdminLayout } from './components/admin/AdminLayout'
import { AdminDashboard, QuestionsList, QuestionEditor, QuestionsImport, ExamsList, ExamsImport, ExamEditor, CategoriesList, CategoriesImport, CategoryEditor, UsersList, AdminUserDetail, ActivityPage, SettingsPage } from './pages/admin'
import { useAuthStore } from './store/useAuthStore'
import { AuthSessionProvider } from './components/AuthSessionProvider'
import { AnalyticsTracker } from './components/AnalyticsTracker'
import { DocumentTitle } from './components/DocumentTitle'
import { AuthModal } from './components/AuthModal'
import { STORAGE_PREFIX } from './lib/brand'
import { useResolvedTheme } from './lib/theme'
import { isStoreClient } from './lib/storeClient'

function EngagementProvider() {
  useEngagement();
  useSyncGamification();
  return null;
}

function GuestLimitLoader() {
  const loadGuestExamLimit = useAuthStore((s) => s.loadGuestExamLimit);
  useEffect(() => { loadGuestExamLimit(); }, [loadGuestExamLimit]);
  return null;
}

function RootRedirect() {
  const onboarded = localStorage.getItem(`${STORAGE_PREFIX}-onboarded`);
  if (onboarded === 'true') return <Navigate to="/dashboard" replace />;
  return <OnboardingPage />;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuthStore();
  const [showAuth, setShowAuth] = useState(!isAuthenticated);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-app flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-xl font-bold text-fg mb-2">Espace Admin</h1>
        <p className="text-sm text-fg-subtle mb-6">Connectez-vous avec un compte administrateur.</p>
        <button
          onClick={() => setShowAuth(true)}
          className="px-5 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-semibold"
        >
          Se connecter
        </button>
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
      </div>
    );
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <AdminLayout>{children}</AdminLayout>;
}

function App() {
  const resolvedTheme = useResolvedTheme();
  useEffect(() => { isStoreClient(); }, []);
  // Config du module de paiement (interrupteur admin, plans, quotas gratuits)
  // hydratée depuis le backend : décision globale, pas locale au navigateur.
  usePaymentBootstrap();
  // Préfixe d'URL du canal (Maxit / M-Pesa) : passé en basename, il « suit »
  // l'utilisateur sur toutes les routes sans toucher aux <Link>/navigate.
  const basename = getRouterBasename();
  return (
    <BrowserRouter basename={basename}>
      <AuthSessionProvider>
        <AnalyticsTracker />
        <DocumentTitle />
        <EngagementProvider />
        <GuestLimitLoader />
        <Toaster position="top-center" theme={resolvedTheme} richColors />
        <Routes>
        <Route path="/" element={<RootRedirect />} />

        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/legal/:section" element={<LegalPage />} />
        <Route path="/cgu" element={<Navigate to="/legal/cgu" replace />} />
        <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />

        {/* Payment Module (routes inertes quand payments.enabled = false) */}
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/checkout/:planId" element={<CheckoutPage />} />
        <Route path="/payment/return" element={<PaymentReturnPage />} />
        <Route path="/payment/mock-gateway" element={<MockGatewayPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />

        <Route path="/dashboard" element={<Layout><DashboardPage /></Layout>} />
        <Route path="/certifications" element={<Layout><CertificationsPage /></Layout>} />
        <Route path="/exam/:examId" element={<Layout><ExamDetailPage /></Layout>} />
        <Route path="/exam/:examId/practice/:categoryId" element={<Layout><QuotaGate kind="practice"><PracticePage /></QuotaGate></Layout>} />
        <Route path="/exam/:examId/simulation" element={<Layout><QuotaGate kind="simulation"><SimulationPage /></QuotaGate></Layout>} />
        <Route path="/labs" element={<Layout><QuotaGate kind="simulation"><LabPage /></QuotaGate></Layout>} />
        <Route path="/stats" element={<Layout><StatsPage /></Layout>} />
        <Route path="/leaderboard" element={<Layout><LeaderboardPage /></Layout>} />
        <Route path="/profile" element={<Layout><ProfilePage /></Layout>} />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/exams" element={<AdminRoute><ExamsList /></AdminRoute>} />
        <Route path="/admin/exams/import" element={<AdminRoute><ExamsImport /></AdminRoute>} />
        <Route path="/admin/exams/new" element={<AdminRoute><ExamEditor /></AdminRoute>} />
        <Route path="/admin/exams/:id" element={<AdminRoute><ExamEditor /></AdminRoute>} />
        <Route path="/admin/categories" element={<AdminRoute><CategoriesList /></AdminRoute>} />
        <Route path="/admin/categories/import" element={<AdminRoute><CategoriesImport /></AdminRoute>} />
        <Route path="/admin/categories/new" element={<AdminRoute><CategoryEditor /></AdminRoute>} />
        <Route path="/admin/categories/:id" element={<AdminRoute><CategoryEditor /></AdminRoute>} />
        <Route path="/admin/questions" element={<AdminRoute><QuestionsList /></AdminRoute>} />
        <Route path="/admin/questions/import" element={<AdminRoute><QuestionsImport /></AdminRoute>} />
        <Route path="/admin/questions/new" element={<AdminRoute><QuestionEditor /></AdminRoute>} />
        <Route path="/admin/questions/:id" element={<AdminRoute><QuestionEditor /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><UsersList /></AdminRoute>} />
        <Route path="/admin/users/:id" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
        <Route path="/admin/activity" element={<AdminRoute><ActivityPage /></AdminRoute>} />
        <Route path="/admin/payments" element={<AdminRoute><PaymentAdminPage /></AdminRoute>} />
        <Route path="/admin/payments/transactions" element={<AdminRoute><PaymentTransactionsPage /></AdminRoute>} />
        <Route path="/admin/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthSessionProvider>
    </BrowserRouter>
  )
}

export default App
